// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Button from "@awc/MobiusOverrides/Button/Button";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import { RequestRmaButtonsStyles } from "@insite/content-library/Widgets/RequestRma/RequestRmaButtons";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { FC, useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    pageTitle: getCurrentPage(state).fields.title,
    isVmiOrderDetailsPage: state.pages.orderDetails.isVmiOrderDetailsPage,
    // XNG-Changes
    pageLink: getPageLinkByPageType(state, "OrderDetailsPage"),
});

export interface RequestRmaHeaderStyles {
    headerGridContainer?: GridContainerProps;
    title: TypographyProps;
    titleGridItem: GridItemProps;
    buttonsGridItem?: GridItemProps;
    requestRmaButtons?: RequestRmaButtonsStyles;
    // XNG-Changes
    returnLinkGridItem?: GridItemProps;
    navigationButton?: ButtonPresentationProps;
}

export const headerStyles: RequestRmaHeaderStyles = {
    buttonsGridItem: {
        css: css`
            justify-content: flex-end;
        `,
        width: [1, 1, 1, 6, 6],
    },
    titleGridItem: {
        width: [11, 11, 11, 6, 6],
    },
    title: {
        variant: "h2",
        as: "h1",
    },
};

const styles = headerStyles;

// XNG-Changes
type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasHistory;

const RequestRmaHeader: FC<Props> = ({ pageTitle, isVmiOrderDetailsPage, pageLink, history }) => {
    const { value: order } = useContext(OrderStateContext);

    // XNG-Changes
    const goBack = () => {
        history.push(`${pageLink}?orderNumber=${order?.webOrderNumber || order?.erpOrderNumber}`);
    };

    return (
        <GridContainer {...styles.headerGridContainer}>
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.title}>{pageTitle}</Typography>
            </GridItem>
            {/* XNG-Changes:add from classic */}
            {order && (
                <GridItem {...styles.returnLinkGridItem}>
                    {/* <OrderDetailPageTypeLink
                        title={translate("Return to Order Details")}
                        orderNumber={order.webOrderNumber || order.erpOrderNumber}
                        isVmiOrder={!!order.vmiLocationId}
                        isVmiOrderDetailsPage={isVmiOrderDetailsPage}
                    /> */}
                    {/* XNG-Changes */}
                    <Button {...styles.navigationButton} onClick={goBack}>
                        {translate("Return to Order Details")}
                    </Button>
                </GridItem>
            )}
            {/* XNG-Changes */}
            {/* <GridItem {...styles.buttonsGridItem}>
                <RequestRmaButtons extendedStyles={styles.requestRmaButtons} />
            </GridItem> */}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, null)(withHistory(RequestRmaHeader)),
    definition: {
        displayName: "Page Header",
        allowedContexts: ["RequestRmaPage"],
        fieldDefinitions: [],
        group: "Return Request (RMA)",
    },
};

export default widgetModule;
